<template>
  <Dialog
    :open="modelValue"
    class="relative z-50"
    @close="emit('update:modelValue', false)"
  >
    <div class="fixed inset-0 bg-black/30" />
    <div class="fixed inset-0 flex items-center justify-center p-4">
      <DialogPanel class="w-full max-w-lg rounded bg-white p-6">
        <div class="mb-4 flex items-center justify-between">
          <DialogTitle class="text-lg font-medium">Editar Variaciones</DialogTitle>
          <button @click="emit('update:modelValue', false)">
            <XMarkIcon class="size-6 text-gray-400" />
          </button>
        </div>
        <div class="mb-4 flex items-center space-x-3 rounded-lg bg-gray-50 p-3">
          <img
            :src="productImageToShow"
            alt=""
            class="size-12 shrink-0 rounded object-cover"
          />
          <div>
            <div class="font-medium text-gray-900">{{ product.name }}</div>
            <div class="text-sm text-gray-500">SKU: {{ product.sku }}</div>
          </div>
        </div>
        <form @submit="onSubmit">
          <div class="mb-4">
            <p class="mb-3 text-sm font-medium text-gray-700">Configurar atributos del producto:</p>
            <div class="space-y-3">
              <div>
                <label class="mb-1 block text-sm font-medium">
                  {{ getAttributeLabel(productGroup.attribute1Type) }}
                </label>
                <FormInput
                  v-model="form.attribute1"
                  class="mb-1"
                  :placeholder="`Ej: ${getAttributeExample(productGroup.attribute1Type)}`"
                  type="text"
                  name="attribute1"
                  label-name="Atributo 1"
                  :rules="{ required: false }"
                />
              </div>
              <div v-if="productGroup.attribute2Type !== 'not_set'">
                <label class="mb-1 block text-sm font-medium">
                  {{ getAttributeLabel(productGroup.attribute2Type) }}
                </label>
                <FormInput
                  v-model="form.attribute2"
                  class="mb-1"
                  :placeholder="`Ej: ${getAttributeExample(productGroup.attribute2Type)}`"
                  type="text"
                  name="attribute2"
                  label-name="Atributo 2"
                  :rules="{ required: false }"
                />
              </div>
              <div v-if="productGroup.attribute3Type !== 'not_set'">
                <label class="mb-1 block text-sm font-medium">
                  {{ getAttributeLabel(productGroup.attribute3Type) }}
                </label>
                <FormInput
                  v-model="form.attribute3"
                  class="mb-1"
                  :placeholder="`Ej: ${getAttributeExample(productGroup.attribute3Type)}`"
                  type="text"
                  name="attribute3"
                  label-name="Atributo 3"
                  :rules="{ required: false }"
                />
              </div>
            </div>

            <div class="mt-3 rounded-md bg-blue-50 p-3 text-sm text-blue-700">
              <p class="font-medium">💡 Tip:</p>
              <p class="mt-1">Estos atributos ayudan a los clientes a encontrar la variación exacta que necesitan.</p>
            </div>
          </div>
          <div class="flex justify-end space-x-2">
            <button
              type="button"
              class="rounded border px-4 py-2 text-gray-600 hover:bg-gray-50"
              @click="emit('update:modelValue', false)"
            >
              Cancelar
            </button>
            <button
              type="submit"
              class="rounded bg-lk-green px-4 py-2 text-white hover:bg-lk-green-deep disabled:bg-gray-400"
              :disabled="isSubmitting"
            >
              {{ isSubmitting ? 'Guardando...' : 'Guardar' }}
            </button>
          </div>
        </form>
      </DialogPanel>
    </div>
  </Dialog>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { Dialog, DialogPanel, DialogTitle } from '@headlessui/vue';
import { XMarkIcon } from '@heroicons/vue/24/solid';
import { useForm } from 'vee-validate';
import { useMutation } from 'vue-query';
import productsApi from 'api/products';
import useMessageStore from 'stores/message-store';
import useProductImage from './use/product-image';
import FormInput from './shared/form-input.vue';

const props = defineProps<{
  modelValue: boolean;
  product: Product;
  productGroup: ProductGroup;
}>();

const emit = defineEmits<{
  'update:modelValue': [value: boolean];
  'attributes-updated': [product: Product];
}>();

const messageStore = useMessageStore();
const { productImageToShow } = useProductImage(props.product);

const { handleSubmit, isSubmitting } = useForm();

const form = ref({
  attribute1: props.product.attribute1 || '',
  attribute2: props.product.attribute2 || '',
  attribute3: props.product.attribute3 || '',
});

function getAttributeLabel(attributeType: string): string {
  const labels: Record<string, string> = {
    size: 'Talla',
    color: 'Color',
    model: 'Modelo',
    not_set: 'Sin definir',
  };
  return labels[attributeType] || attributeType;
}

function getAttributeExample(attributeType: string): string {
  const examples: Record<string, string> = {
    size: 'S, M, L, XL',
    color: 'Rojo, Azul, Verde',
    model: 'Clásico, Premium, Deluxe',
  };
  return examples[attributeType] || 'Valor del atributo';
}

const updateMutation = useMutation(
  () =>
    productsApi.update(props.product.id, {
      product: {
        attribute_1: form.value.attribute1,
        attribute_2: form.value.attribute2,
        attribute_3: form.value.attribute3,
      } as any,
    }),
  {
    onSuccess: () => {
      messageStore.showMessage('Variaciones actualizadas exitosamente', 'success');
      const updatedProduct = {
        ...props.product,
        attribute1: form.value.attribute1,
        attribute2: form.value.attribute2,
        attribute3: form.value.attribute3,
      };

      emit('attributes-updated', updatedProduct);
      emit('update:modelValue', false);
    },
    onError: (error: any) => {
      messageStore.showMessage(error.response?.data?.error || 'Error al actualizar variaciones', 'error');
    },
  },
);

const onSubmit = handleSubmit(() => {
  updateMutation.mutate();
});
</script>
